import { calculateNodeHeight } from './nodes.js';
import { buildAdjacencyList, calculateEdgePaths } from './edges.js';
import { calcWidth, assign, resolveCollisions, normalizeToBounds, placeDisconnectedNodes } from './position-layout.js';
import { NODE_MIN_HEIGHT } from '../../../../Config/ExistingVisaTreeViewConsts.js';

// Re-export utility functions for external use
export { cleanText, wrapText, clearTextCaches, getTextCacheStats } from './text.js';
export { getNodeCategory } from './nodes.js';
export { getEdgeColor } from './edges.js';


/**
 * Unified engine interface.
 * 
 * MODE 1: Full layout calculation
 *   engine(treeData)
 *   Returns: { positions, nodeHeights, children, edges, bounds }
 * 
 * MODE 2: Edge recalculation on node position update
 *   engine(currentNodes, childrenMap)
 *   Returns: { edges }
 * 
 * @param {Object|Array} treeDataOrNodes - treeData object or array of nodes
 * @param {Object} childrenMapOrUndefined - childrenMap (indicates edge recalc mode)
 * @returns {Object} Layout or edge data
 */

export default function engine(treeDataOrNodes, childrenMap) {
  // Edge recalculation mode: second param indicates recalculate for node position updates
  if (childrenMap !== undefined) {
    const edges = recalculateEdgesForNodes(treeDataOrNodes, childrenMap);
    return { edges };
  }

  // Full layout mode: calculate complete layout from treeData
  const { positions, nodeHeights, children } = calculateTreeLayout(treeDataOrNodes);
  const edges = calculateEdgePaths(positions, nodeHeights, children);

  return {
    positions,
    children,
    edges
  };
}

// ── Main Layout Pipeline ────────────────────────────────────────────────
/**
 * Calculate the complete tree layout.
 * 
 * Pipeline:
 * 1. Build adjacency list from edges
 * 2. Calculate node heights
 * 3. Phase 1: Compute subtree widths (bottom-up)
 * 4. Phase 2: Assign positions (top-down)
 * 5. Phase 3: Resolve collisions (iterative)
 * 6. Phase 4: Normalize to canvas origin
 * 7. Handle disconnected nodes
 * 
 * @param {Object} treeData - { nodes, edges, root_node }
 * @returns {Object} Layout result with positions, heights, bounds, children
 */
const calculateTreeLayout = (treeData) => {
  const { nodes, edges, root_node } = treeData;
  
  // Handle empty tree
  if (Object.keys(nodes).length === 0) {
    return {
      positions: {},
      nodeHeights: {},
      bounds: { width: 0, height: 0 },
      children: {},
    };
  }

  // Build adjacency list
  const children = buildAdjacencyList(nodes, edges);

  // Pre-calculate all node heights
  const nodeHeights = {};
  for (const nid in nodes) {
    nodeHeights[String(nid)] = calculateNodeHeight(nodes[nid]);
  }

  const rootId = root_node ? String(root_node.id) : null;
  const positions = {};
  const widths = {};

  // Phase 1: Calculate subtree widths (bottom-up)
  if (rootId) {
    calcWidth(rootId, children, widths);
  }

  // Phase 2: Assign positions (top-down)
  if (rootId) {
    assign(rootId, 0, 0, children, widths, positions);
  }

  // Phase 3: Resolve collisions (iterative)
  resolveCollisions(positions, nodeHeights, children);

  // Handle disconnected nodes
  const allNodeIds = Object.keys(nodes).map(String);
  const disconnected = allNodeIds.filter(id => !positions[id]);
  if (disconnected.length > 0) {
    placeDisconnectedNodes(disconnected, positions, nodeHeights);
  }

  // Phase 4: Normalize to canvas origin and compute bounds
  const bounds = normalizeToBounds(positions, nodeHeights);

  return {
    positions,
    nodeHeights,
    bounds,
    children,
  };
};

/**
 * Recalculate edge paths based on current node positions.
 * Used when nodes are moved/repositioned in the UI.
 * 
 * @param {Array} currentNodes - Array of React Flow nodes with position and height
 * @param {Object} childrenMap - Parent-child adjacency map
 * @returns {Array} Updated edge paths for React Flow
 */
const recalculateEdgesForNodes = (currentNodes, childrenMap) => {
  const positions = {};
  const nodeHeights = {};
  
  for (const node of currentNodes) {
    positions[node.id] = node.position;
    nodeHeights[node.id] = node.height || NODE_MIN_HEIGHT;
  }

  return calculateEdgePaths(positions, nodeHeights, childrenMap);
};
